import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import Layout from '../components/Layout';
import { useApp, resolveImg } from '../context/AppContext';
import { currencySymbols, rates } from '../mock/mock';
import useSEO from '../hooks/useSEO';

const L = {
  de: {
    title: 'Preise & Tarife', subtitle: 'Transparente Festpreise für alle Fahrzeugklassen – ohne versteckte Kosten.',
    perKmHead: 'Preise pro Fahrzeugklasse', baseFare: 'Grundpreis', perKmRate: 'Pro Kilometer', minFare: 'Mindestpreis',
    book: 'Jetzt buchen', routesHead: 'Beliebte Strecken – Beispielpreise', route: 'Strecke', distance: 'Distanz',
    note: '* Alle Preise sind Festpreise inkl. MwSt., Wartezeit und Gebühren. Endpreis abhängig von Route und Fahrzeug.',
    passengers: 'Passagiere', bags: 'Gepäck', from: 'ab',
  },
  en: {
    title: 'Prices & Rates', subtitle: 'Transparent fixed prices for every vehicle class — no hidden costs.',
    perKmHead: 'Rates per vehicle class', baseFare: 'Base fare', perKmRate: 'Per kilometer', minFare: 'Minimum fare',
    book: 'Book now', routesHead: 'Popular routes – example prices', route: 'Route', distance: 'Distance',
    note: '* All prices are fixed and include VAT, waiting time and fees. Final price depends on route and vehicle.',
    passengers: 'passengers', bags: 'bags', from: 'from',
  },
  fr: {
    title: 'Prix & Tarifs', subtitle: 'Prix fixes et transparents pour chaque classe de véhicule — sans frais cachés.',
    perKmHead: 'Tarifs par classe de véhicule', baseFare: 'Prix de base', perKmRate: 'Par kilomètre', minFare: 'Prix minimum',
    book: 'Réserver', routesHead: 'Trajets populaires – prix exemples', route: 'Trajet', distance: 'Distance',
    note: '* Tous les prix sont fixes, TVA, temps d’attente et frais inclus. Le prix final dépend du trajet et du véhicule.',
    passengers: 'passagers', bags: 'bagages', from: 'dès',
  },
  it: {
    title: 'Prezzi & Tariffe', subtitle: 'Prezzi fissi e trasparenti per ogni classe di veicolo — senza costi nascosti.',
    perKmHead: 'Tariffe per classe di veicolo', baseFare: 'Prezzo base', perKmRate: 'Al chilometro', minFare: 'Prezzo minimo',
    book: 'Prenota ora', routesHead: 'Tratte popolari – prezzi esempio', route: 'Tratta', distance: 'Distanza',
    note: '* Tutti i prezzi sono fissi e includono IVA, tempo di attesa e commissioni. Il prezzo finale dipende da tratta e veicolo.',
    passengers: 'passeggeri', bags: 'bagagli', from: 'da',
  },
};

// Example popular routes with approximate driving distance (km).
const exampleRoutes = [
  { route: 'Zürich Airport → St. Moritz', km: 200 },
  { route: 'Zürich Airport → Davos', km: 155 },
  { route: 'Zürich Airport → Zermatt', km: 245 },
  { route: 'Zürich Airport → Luzern', km: 55 },
  { route: 'Geneva Airport → Verbier', km: 160 },
  { route: 'Zürich Airport → Interlaken', km: 120 },
];

// Vehicle classes shown as columns in the routes table.
const routeClasses = ['economy', 'business', 'first'];

const PricesPage = () => {
  const { lang, currency, vehicles, pricing, priceFor } = useApp();
  const PRICING = pricing;
  const s = L[lang] || L.en;
  const fmt = (n) => `${currencySymbols[currency]} ${Math.round(n * rates[currency])}`;
  const fmtKm = (n) => `${currencySymbols[currency]} ${(n * rates[currency]).toFixed(2)}`;

  const seo = {
    de: 'Preise & Tarife | CAB.TAXI Zürich',
    en: 'Prices & Rates | CAB.TAXI Zurich',
    fr: 'Prix & Tarifs | CAB.TAXI Zurich',
    it: 'Prezzi & Tariffe | CAB.TAXI Zurigo',
  }[lang];
  useSEO(seo, s.subtitle);

  const cols = routeClasses.map((id) => vehicles.find((v) => v.id === id)).filter(Boolean);

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[color:var(--brand-dark)] text-white py-16" data-testid="prices-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase mb-5">
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--brand-accent)' }} /> CAB.TAXI
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">{s.title}</h1>
          <p className="mt-4 max-w-2xl text-white/70 leading-relaxed">{s.subtitle}</p>
        </div>
      </section>

      {/* Per-km rate cards */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[color:var(--brand-dark)] mb-8">{s.perKmHead}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="prices-vehicle-grid">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`price-card-${v.id}`}>
                <div className="h-36 bg-[color:var(--brand-muted)] overflow-hidden">
                  <img src={resolveImg(v.img)} alt={v.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-lg font-extrabold text-[color:var(--brand-dark)]">{v.name}</h3>
                  <p className="text-sm text-gray-400">{v.model}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {v.pax}</span>
                    <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {v.bags}</span>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between"><span className="text-gray-400">{s.baseFare}</span><b className="text-[color:var(--brand-dark)]" data-testid={`price-base-${v.id}`}>{fmt(Number(v.baseFare) || PRICING.BASE * v.priceFactor)}</b></div>
                    <div className="flex items-center justify-between"><span className="text-gray-400">{s.perKmRate}</span><b className="text-[color:var(--brand-dark)]" data-testid={`price-perkm-${v.id}`}>{fmtKm(Number(v.perKm) || PRICING.PER_KM * v.priceFactor)}</b></div>
                    <div className="flex items-center justify-between"><span className="text-gray-400">{s.minFare}</span><b style={{ color: 'var(--brand-accent)' }} data-testid={`price-min-${v.id}`}>{fmt(Number(v.minFare) || PRICING.MIN * v.priceFactor)}</b></div>
                  </div>
                  <Link to="/booking" className="accent-btn mt-5 text-center rounded-full px-4 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid={`price-book-${v.id}`}>
                    {s.book} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular routes example prices */}
      <section className="py-14 bg-[color:var(--brand-muted)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[color:var(--brand-dark)] mb-8">{s.routesHead}</h2>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto" data-testid="prices-routes-table">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="bg-[color:var(--brand-dark)] text-white text-left">
                  <th className="py-4 px-5 font-semibold">{s.route}</th>
                  <th className="py-4 px-5 font-semibold whitespace-nowrap">{s.distance}</th>
                  {cols.map((v) => (
                    <th key={v.id} className="py-4 px-5 font-semibold text-right whitespace-nowrap">{v.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {exampleRoutes.map((r, i) => (
                  <tr key={r.route} className={i % 2 ? 'bg-[color:var(--brand-muted)]/40' : ''} data-testid={`route-row-${i}`}>
                    <td className="py-4 px-5 font-semibold text-[color:var(--brand-dark)]">
                      <span className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} /> {r.route}</span>
                    </td>
                    <td className="py-4 px-5 text-gray-500 whitespace-nowrap">{r.km} km</td>
                    {cols.map((v) => (
                      <td key={v.id} className="py-4 px-5 text-right font-bold text-[color:var(--brand-dark)] whitespace-nowrap">
                        <span className="text-xs font-normal text-gray-400 mr-1">{s.from}</span>{fmt(priceFor(r.km, v))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-xs text-gray-500 max-w-3xl">{s.note}</p>
          <div className="mt-8">
            <Link to="/booking" className="accent-btn inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold" data-testid="prices-cta-book">
              {s.book} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricesPage;
